import * as React from 'react';
import { Link } from 'react-router-dom';

import { useCan } from '@/domains/auth';
import {
  countScope,
  SCOPE_LABEL,
  scopeListPath,
  searchPath,
  searchedScopes,
  type SearchScope
} from '../record-search';

/**
 * Every list's results for one search, one press away (SR-414; the owner's Q1, the plan's D21) —
 * drawn under the search box of People, Teams, Firms and the SEC ADV register while their `q` is
 * set: "People 3 · Teams 1 · Firms 2 · SEC ADV register 1", one link per list this account opens,
 * each to that list under `q` alone. The client asked to reach every result of every type from a
 * search (SR-386, SR-401); a page of its own for the search was not wanted, so each list says the
 * others' totals and leads to them.
 *
 * **It narrows nothing.** The page's own search stays the page's: the current list is marked
 * `aria-current` and counted by the page's own read (`total`, null while it is out or failed), so the
 * number beside it is the "N found" above the table. Every other list is a read of its own, one
 * row asked under `q` alone — the total its link opens — read again only when `q` changes, never
 * polled. A count still out is a pulsing shape the width of a short number, "counting" to a
 * screen reader, and fades in as it lands (the owner's ask of 2026-10-15: no blank that a number
 * jumps into); one that failed says it could not be counted, in words rather than a `title`; a
 * zero is a link like any other, since it is still an answer. Under reduced motion nothing moves.
 * **A count is drawn only under the `q` it was read for** (`counts.q`), so a new search never
 * shows the last one's totals, for a frame or because a cancelled read's body landed late.
 */
type Counted = { state: 'loading' } | { state: 'done'; total: number } | { state: 'failed' };

export function SearchScopes({
  scope,
  q,
  total,
  failed = false
}: {
  /** The list this strip is drawn on. */
  scope: SearchScope;
  q: string;
  /** The page's own read's total, or null while it is out. */
  total: Nullable<number>;
  /** Whether the page's own read under this query failed. */
  failed?: boolean;
}) {
  const can = useCan();
  const scopes = searchedScopes(can);
  const others = scopes.filter((other) => other !== scope).join(' ');
  const [counts, setCounts] = React.useState<{
    q: string;
    of: Partial<Record<SearchScope, Counted>>;
  }>({ q: '', of: {} });

  React.useEffect(() => {
    if (q === '' || others === '') return undefined;
    const controller = new AbortController();
    setCounts({ q, of: {} });
    const land = (other: SearchScope, counted: Counted) => {
      // A read the next search cancelled can still have its body in hand.
      if (controller.signal.aborted) return;
      setCounts((now) => (now.q === q ? { q, of: { ...now.of, [other]: counted } } : now));
    };
    for (const other of others.split(' ') as SearchScope[]) {
      countScope(other, q, controller.signal).then(
        (total) => land(other, { state: 'done', total }),
        () => land(other, { state: 'failed' })
      );
    }
    return () => controller.abort();
  }, [q, others]);

  if (q === '' || scopes.length < 2) return null;

  const countOf = (other: SearchScope): Counted => {
    if (other !== scope) {
      return (counts.q === q ? counts.of[other] : undefined) ?? { state: 'loading' };
    }
    if (total !== null) return { state: 'done', total };
    return failed ? { state: 'failed' } : { state: 'loading' };
  };

  return (
    <nav
      aria-label={`Results for “${q}”`}
      data-search-scopes
      className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
    >
      <span className="text-muted-foreground">Results for “{q}”:</span>
      <ul className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {scopes.map((other, index) => {
          const counted = countOf(other);
          const current = other === scope;
          return (
            <li key={other} className="flex items-baseline gap-x-2">
              {index > 0 && (
                <span aria-hidden className="text-muted-foreground">
                  ·
                </span>
              )}
              <Link
                to={scopeListPath(other, q)}
                aria-current={current ? 'page' : undefined}
                data-scope={other}
                className={
                  current
                    ? 'font-semibold text-foreground underline-offset-4 hover:underline'
                    : 'text-[var(--color-sapphire)] underline-offset-4 hover:underline'
                }
              >
                {SCOPE_LABEL[other]} <Count counted={counted} />
              </Link>
            </li>
          );
        })}
        <li className="flex items-baseline gap-x-2">
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          {/* Every list's matches on one page (SR-463). */}
          <Link
            to={searchPath(q)}
            data-all-results
            className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            All results
          </Link>
        </li>
      </ul>
    </nav>
  );
}

/** A count as the strip says it: a pulsing shape while out, the number faded in, or that it could
 *  not be counted. Each state its own element, so the fade runs as the number lands. */
function Count({ counted }: { counted: Counted }) {
  switch (counted.state) {
    case 'loading':
      return (
        <span key="loading" data-count data-counting>
          <span
            aria-hidden
            className="inline-block h-3 w-7 animate-pulse rounded-sm bg-muted align-[-0.125em] motion-reduce:animate-none"
          />
          <span className="sr-only">counting</span>
        </span>
      );
    case 'failed':
      return (
        <span key="failed" data-count className={LANDED}>
          could not be counted
        </span>
      );
    case 'done':
      return (
        <span key="done" data-count className={LANDED}>
          {counted.total}
        </span>
      );
  }
}

/** A count landing: faded in; still under reduced motion. */
const LANDED = 'animate-in fade-in-0 duration-300 motion-reduce:animate-none';
